import React from 'react';
import {
    Box,
    Pagination,
    Typography,
    Skeleton,
    useMediaQuery,
    useTheme
} from '@mui/material';

// CSS for shimmer animation
const shimmerKeyframes = `
@keyframes shimmer {
  0% {
    transform: translateX(-100%);
  }
  100% {
    transform: translateX(100%);
  }
}
`;

export interface DataTableColumn {
    key: string;
    label: string;
    render?: (value: any, row: any) => React.ReactNode;
    width?: string | number;
}

export interface DataTableProps {
    columns: DataTableColumn[];
    data: any[];
    loading?: boolean;
    total?: number;
    currentPage?: number;
    limit?: number;
    onPageChange?: (page: number) => void;
    emptyMessage?: string;
    showPagination?: boolean;
}

// Skeleton Row Component
const SkeletonRow: React.FC<{ columns: DataTableColumn[] }> = ({ columns }) => (
    <Box
        sx={{
            display: 'grid',
            gridTemplateColumns: `repeat(${columns.length}, minmax(120px, 1fr))`,
            gap: '12px',
            padding: '16px',
            backgroundColor: '#FAF9F9',
            borderRadius: '12px',
            boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
            marginBottom: '16px',
            position: 'relative',
            overflow: 'hidden'
        }}
    >
        {columns.map((column, index) => (
            <Box key={index} sx={{ 
                position: 'relative', 
                overflow: 'hidden',
                display: 'flex',
                alignItems: 'center',
                minHeight: '20px'
            }}>
                <Skeleton
                    variant="text"
                    width="100%"
                    height={20}
                    sx={{
                        backgroundColor: '#E0E0E0',
                        '&::after': {
                            content: '""',
                            position: 'absolute',
                            top: 0,
                            left: 0,
                            width: '100%',
                            height: '100%',
                            background: 'linear-gradient(90deg, transparent, rgba(255,255,255,0.6), transparent)',
                            animation: 'shimmer 1.5s infinite',
                            transform: 'translateX(-100%)'
                        }
                    }}
                />
            </Box>
        ))}
    </Box>
);

const DataTable: React.FC<DataTableProps> = ({
    columns,
    data,
    loading = false,
    total = 0,
    currentPage = 1,
    limit = 10,
    onPageChange,
    emptyMessage = 'No data found',
    showPagination = true
}) => {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
    
    const handlePageChange = (event: React.ChangeEvent<unknown>, page: number) => {
        if (onPageChange) {
            onPageChange(page);
        }
    };

    return (
        <>
            <style>{shimmerKeyframes}</style>
            <Box sx={{ overflowX: 'auto' }}>
                {/* Table Container */}
                <Box sx={{ minWidth: `${columns.length * 140}px` }}>
                    {/* Table Header */}
                    <Box
                        sx={{
                            display: 'grid',
                            gridTemplateColumns: `repeat(${columns.length}, minmax(120px, 1fr))`,
                            gap: '12px',
                            padding: '16px 12px',
                            backgroundColor: '#fff',
                            borderRadius: '16px 16px 0 0',
                            borderBottom: '1px solid #E0E0E0'
                        }}
                    >
                        {columns.map((column) => (
                            <Typography
                                key={column.key}
                                sx={{
                                    fontWeight: 600,
                                    fontSize: '13px',
                                    color: '#49454F',
                                    minWidth: column.width || '120px',
                                    whiteSpace: 'nowrap',
                                    overflow: 'hidden',
                                    textOverflow: 'ellipsis'
                                }}
                            >
                                {column.label}
                            </Typography>
                        ))}
                    </Box>

                    {/* Table Body */}
                    <Box sx={{ mt: 2 }}>
                        {loading ? (
                            // Skeleton Loading State
                            <>
                                {Array.from({ length: 5 }).map((_, index) => (
                                    <SkeletonRow key={index} columns={columns} />
                                ))}
                            </>
                        ) : data.length === 0 ? (
                            <Box
                                sx={{
                                    textAlign: 'center',
                                    py: 4,
                                    backgroundColor: '#fff',
                                    borderRadius: '0 0 16px 16px'
                                }}
                            >
                                <Typography color="text.secondary">
                                    {emptyMessage}
                                </Typography>
                            </Box>
                        ) : (
                            data.map((row, rowIndex) => (
                                <Box
                                    key={rowIndex}
                                    sx={{
                                        display: 'grid',
                                        gridTemplateColumns: `repeat(${columns.length}, minmax(120px, 1fr))`,
                                        gap: '12px',
                                        padding: '16px',
                                        backgroundColor: '#FAF9F9',
                                        borderRadius: '12px',
                                        boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
                                        marginBottom: rowIndex < data.length - 1 ? '16px' : '0',
                                        fontSize: '13px',
                                        color: '#49454F'
                                    }}
                                >
                                    {columns.map((column) => (
                                        <Box 
                                            key={column.key}
                                            sx={{
                                                minWidth: column.width || '120px',
                                                whiteSpace: 'nowrap',
                                                overflow: 'hidden',
                                                textOverflow: 'ellipsis',
                                                display: 'flex',
                                                alignItems: 'center',
                                                minHeight: '20px'
                                            }}
                                        >
                                            {column.render 
                                                ? column.render(row[column.key], row)
                                                : row[column.key] || '—'
                                            }
                                        </Box>
                                    ))}
                                </Box>
                            ))
                        )}
                    </Box>
                </Box>

                {/* Pagination - Only show when not loading and has data */}
                {showPagination && !loading && total > 0 && (
                    <Box 
                        mt={3} 
                        display='flex' 
                        flexDirection={isMobile ? 'column' : 'row'}
                        justifyContent='space-between' 
                        alignItems={isMobile ? 'stretch' : 'center'}
                        gap={isMobile ? 2 : 0}
                        sx={{
                            backgroundColor: '#fff',
                            padding: isMobile ? '12px 16px' : '16px 24px',
                            borderRadius: '12px',
                            boxShadow: '0 1px 3px rgba(0,0,0,0.1)'
                        }}
                    >
                        <Typography 
                            fontSize={isMobile ? '12px' : '14px'} 
                            color='#49454F'
                            textAlign={isMobile ? 'center' : 'left'}
                        >
                            Showing <b>{((currentPage - 1) * limit) + 1}</b> to <b>{Math.min(currentPage * limit, total)}</b> of <b>{total}</b> results
                        </Typography>
                        <Box 
                            display='flex' 
                            justifyContent={isMobile ? 'center' : 'flex-end'}
                            width={isMobile ? '100%' : 'auto'}
                        >
                            <Pagination 
                                count={Math.ceil(total / limit)} 
                                page={currentPage} 
                                onChange={handlePageChange}
                                size={isMobile ? 'small' : 'medium'}
                                siblingCount={isMobile ? 0 : 1}
                                boundaryCount={isMobile ? 1 : 1}
                                showFirstButton={!isMobile}
                                showLastButton={!isMobile}
                                sx={{
                                    '& .MuiPaginationItem-root': {
                                        color: '#666',
                                        fontSize: isMobile ? '12px' : '14px',
                                        fontWeight: 500,
                                        minWidth: isMobile ? '32px' : '40px',
                                        height: isMobile ? '32px' : '40px'
                                    },
                                    '& .Mui-selected': {
                                        backgroundColor: '#203A6B',
                                        color: '#fff',
                                        '&:hover': {
                                            backgroundColor: '#1a2e56'
                                        }
                                    },
                                    '& .MuiPaginationItem-ellipsis': {
                                        fontSize: isMobile ? '12px' : '14px'
                                    }
                                }}
                            />
                        </Box>
                    </Box>
                )}
            </Box>
        </>
    );
};

export default DataTable; 